import type { Mark } from "prosemirror-model";
import type { Transaction } from "prosemirror-state";
import { ReplaceStep, type Step } from "prosemirror-transform";

/**
 * Removes existing suggestion marks from ranges that will be modified by new steps.
 *
 * This prevents nested/duplicate suggestion marks when applying new AI edits
 * on top of existing suggestions. The semantics are: the latest AI suggestion
 * overrides any older suggestions in the same range.
 *
 * Call this BEFORE transformToSuggestionTransaction to ensure clean ranges.
 */
export function stripSuggestionsFromStepRanges(tr: Transaction, steps: Step[]): Transaction {
	const schema = tr.doc.type.schema;
	const insertion = schema.marks["insertion"];
	const deletion = schema.marks["deletion"];
	const modification = schema.marks["modification"];

	if (!insertion || !deletion) {
		return tr;
	}

	for (const step of steps) {
		if (!(step instanceof ReplaceStep)) {
			continue;
		}

		const from = step.from;
		const to = step.to;

		if (from >= to) {
			continue;
		}

		try {
			tr.removeMark(from, to, insertion);
			tr.removeMark(from, to, deletion);
			if (modification) {
				tr.removeMark(from, to, modification);
			}
		} catch {
			// Position may be out of bounds if doc structure changed
		}
	}

	return tr;
}

/**
 * Scans the document and merges adjacent suggestion marks with the same ID.
 * This cleans up fragmented marks that can occur after complex transformations.
 *
 * Note: ProseMirror's mark joining logic typically handles this automatically
 * when applying transactions, so explicit merging may not be necessary.
 * This function is a placeholder for future optimization if needed.
 */
export function mergeAdjacentSuggestionMarks(): void {
	// Intentionally empty - ProseMirror handles mark joining automatically
}

interface RedundantPair {
	delFrom: number;
	delTo: number;
	insFrom: number;
	insTo: number;
}

/**
 * Finds and removes redundant del/ins pairs where the deleted and inserted text are identical.
 *
 * This can happen when:
 *   1. Text is deleted in one change
 *   2. The exact same text is added back in a subsequent change
 *
 * In this case, showing both a deletion and insertion of the same text is confusing to users.
 * Instead, we remove both marks since the net effect is no change.
 *
 * IMPORTANT: We only consider pairs redundant if they have identical text AND identical formatting
 * marks (excluding the del/ins marks themselves). For example, `<del>world</del><ins><b>world</b></ins>`
 * is NOT redundant because the formatting changed.
 *
 * Returns a new transaction with the redundant pairs removed, or the original if none found.
 */
export function coalesceRedundantSuggestions(tr: Transaction): Transaction {
	const schema = tr.doc.type.schema;
	const insertion = schema.marks["insertion"];
	const deletion = schema.marks["deletion"];
	const modification = schema.marks["modification"];

	if (!insertion || !deletion) {
		return tr;
	}

	// Build a set of suggestion mark types to exclude from comparison
	const suggestionMarkTypes = new Set([insertion, deletion]);
	if (modification) {
		suggestionMarkTypes.add(modification);
	}

	// Find all redundant pairs in the document
	const redundantPairs: RedundantPair[] = [];

	tr.doc.descendants((node, pos) => {
		if (!node.isText) {
			return true;
		}

		const delMark = deletion.isInSet(node.marks);
		if (!delMark) {
			return true;
		}

		// Found a deletion - look for an adjacent insertion with the same content
		const delEnd = pos + node.nodeSize;
		const delText = node.text ?? "";
		const suggestionId = delMark.attrs["id"];

		// Check what comes immediately after this deletion
		const $after = tr.doc.resolve(delEnd);
		const nodeAfter = $after.nodeAfter;

		if (!nodeAfter || !nodeAfter.isText) {
			return true;
		}

		const insMark = insertion.isInSet(nodeAfter.marks);
		if (!insMark) {
			return true;
		}

		// Check if they have the same suggestion ID and identical text
		if (insMark.attrs["id"] !== suggestionId || nodeAfter.text !== delText) {
			return true;
		}

		// Check if the formatting marks (excluding suggestion marks) are identical
		const delFormattingMarks = node.marks.filter((m) => !suggestionMarkTypes.has(m.type));
		const insFormattingMarks = nodeAfter.marks.filter((m) => !suggestionMarkTypes.has(m.type));

		// Compare formatting marks - they must be identical for the pair to be redundant
		if (!marksEqual(delFormattingMarks, insFormattingMarks)) {
			return true;
		}

		redundantPairs.push({
			delFrom: pos,
			delTo: delEnd,
			insFrom: delEnd,
			insTo: delEnd + nodeAfter.nodeSize,
		});

		return true;
	});

	if (redundantPairs.length === 0) {
		return tr;
	}

	// Process pairs in reverse order to maintain position validity
	for (let i = redundantPairs.length - 1; i >= 0; i--) {
		const pair = redundantPairs[i];

		// Remove the deletion mark from the deleted text (keeps the text)
		tr.removeMark(pair.delFrom, pair.delTo, deletion);

		// Delete the inserted text entirely (it's a duplicate)
		tr.delete(pair.insFrom, pair.insTo);
	}

	return tr;
}

/**
 * Compares two arrays of marks for equality.
 */
function marksEqual(a: readonly Mark[], b: readonly Mark[]): boolean {
	if (a.length !== b.length) {
		return false;
	}

	// Sort by mark type name for consistent comparison
	const sortedA = [...a].sort((x, y) => x.type.name.localeCompare(y.type.name));
	const sortedB = [...b].sort((x, y) => x.type.name.localeCompare(y.type.name));

	for (let i = 0; i < sortedA.length; i++) {
		if (!sortedA[i].eq(sortedB[i])) {
			return false;
		}
	}

	return true;
}
